"use client"

import * as React from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { cn } from "@sarkiai/utils"
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  Button,
  Avatar,
  AvatarImage,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@sarkiai/ui"
import {
  Menu,
  PanelLeft,
  PanelLeftOpen,
  User,
  Settings,
  LogOut,
  MoreHorizontal,
  Pencil,
  Archive,
  Trash2,
  Check,
  Loader2,
  type LucideIcon,
} from "lucide-react"
import { useTranslations } from "@/components/I18nProvider"
import { toast } from "sonner"
import {
  deleteUserConversation,
  archiveUserConversation,
  updateUserConversationTitle
} from "@/app/account/actions"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label
} from "@sarkiai/ui"
import { useRouter } from "next/navigation"
import { SettingsSVG } from "@/components/icons/SettingsSVG"

export interface NavItem {
  segment: string
  title: string
  icon?: LucideIcon | React.ReactNode
  action?: React.ReactNode
  children?: NavItem[]
}

export interface NavHeader {
  kind: "header"
  title: string
}

export interface NavDivider {
  kind: "divider"
}

export type NavigationItem = NavItem | NavHeader | NavDivider

interface SidebarProps {
  navigation: NavigationItem[]
  isCollapsed: boolean
  onToggleCollapse: () => void
  user?: {
    name?: string | null
    email?: string | null
    image?: string | null
  }
  onSignOut?: () => void
  branding?: {
    logo?: React.ReactNode
    title?: string
    homeUrl?: string
  }
}

function isNavItem(item: NavigationItem): item is NavItem {
  return "segment" in item
}

function isNavHeader(item: NavigationItem): item is NavHeader {
  return "kind" in item && item.kind === "header"
}

function isNavDivider(item: NavigationItem): item is NavDivider {
  return "kind" in item && item.kind === "divider"
}

function NavItemComponent({
  item,
  isCollapsed,
  isSelected,
}: {
  item: NavItem
  isCollapsed: boolean
  isSelected: boolean
}) {
  const Icon = item.icon
  const router = useRouter()
  const t = useTranslations("account")
  const tActions = useTranslations("actions")

  const [isRenameOpen, setIsRenameOpen] = React.useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = React.useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false)
  const [newName, setNewName] = React.useState(item.title)
  const [isProcessing, setIsProcessing] = React.useState(false)

  const chatId = React.useMemo(() => {
    if (item.segment?.startsWith("account/chat/")) {
      return item.segment.split("/").pop()
    }
    return null
  }, [item.segment])

  // "New Chat" item should never be selected
  const isNewChatItem = item.segment === "account/chat"
  const isConversationItem = Boolean(chatId)

  const handleRename = async () => {
    if (!chatId || !newName.trim() || newName === item.title) {
      setIsRenameOpen(false)
      return
    }
    setIsProcessing(true)
    try {
      const res = await updateUserConversationTitle({ chatId, title: newName.trim() })
      if (res?.success) {
        toast.success(t("dialog.conv_update_success"))
        router.refresh()
      } else {
        toast.error("Error updating title")
      }
    } catch (error) {
      toast.error("An error occurred")
    } finally {
      setIsProcessing(false)
      setIsRenameOpen(false)
    }
  }

  const handleArchive = async () => {
    if (!chatId) return
    setIsProcessing(true)
    try {
      const res = await archiveUserConversation({ conversationId: chatId })
      if (res?.success) {
        toast.success(t("dialog.conv_archive_success"))
        router.refresh()
      } else {
        toast.error("Error archiving conversation")
      }
    } catch (error) {
      toast.error("An error occurred")
    } finally {
      setIsProcessing(false)
      setIsArchiveOpen(false)
    }
  }

  const handleDelete = async () => {
    if (!chatId) return
    setIsProcessing(true)
    try {
      const res = await deleteUserConversation({ conversationId: chatId })
      if (res?.success) {
        toast.success(t("dialog.conv_delete_success"))
        router.refresh()
      } else {
        toast.error("Error deleting conversation")
      }
    } catch (error) {
      toast.error("An error occurred")
    } finally {
      setIsProcessing(false)
      setIsDeleteOpen(false)
    }
  }

  return (
    <div className="relative group/nav-item overflow-visible">
      <Link
        href={`/${item.segment}`}
        className={cn(
          "group flex items-center rounded-lg px-2.5 py-1.5 text-sm flex items-center gap-2 transition-all duration-300",
          // New Chat item: normal style, never selected
          isNewChatItem
            ? "text-foreground monochrome-hover"
            : isConversationItem
              // Conversation items: simpler style
              ? cn(
                "monochrome-hover",
                isSelected ? "bg-muted text-foreground" : "text-foreground"
              )
              // Regular nav items
              : cn(
                "monochrome-hover",
                isSelected ? "bg-foreground text-background" : "text-foreground"
              ),

          isCollapsed && "justify-center px-0 h-8 w-8 mx-auto"
        )}
      >
        {/* Only show icon for non-conversation items */}
        {Icon && !isConversationItem && (
          <span className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center transition-colors duration-200",
            isNewChatItem ? "text-foreground group-hover:text-foreground" : (isSelected ? "text-background" : "text-foreground group-hover:text-foreground")
          )}>
            {React.isValidElement(Icon) ? Icon : (typeof Icon === 'function' ? <Icon className="h-3.5 w-3.5" /> : null)}
          </span>
        )}
        {!isCollapsed && (
          <>
            <span className="flex-1 truncate">{item.title}</span>
            {chatId && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon"
                    className={cn(
                      "h-6 w-6 rounded-md opacity-0 group-hover:opacity-100 transition-all duration-300",
                      "rounded-lg bg-transparent shadow-none",
                      "text-foreground monochrome-hover",
                      isSelected && (
                        isConversationItem
                          ? "text-foreground hover:text-foreground hover:bg-foreground/10"
                          : "text-background hover:text-background hover:bg-background/10"
                      )
                    )}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Plus</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="p-1.5 shadow-2xl border border-muted-foreground/20 dark:border-border/50 backdrop-blur-3xl bg-card/90 dark:bg-zinc-900/90 rounded-2xl"
                >
                  <DropdownMenuItem
                    asChild
                    onClick={() => setIsRenameOpen(true)}
                    className="rounded-lg cursor-pointer py-1 px-2 monochrome-hover transition-colors group"
                  >
                    <div className="flex items-center w-full">
                      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-transparent mr-1.5 text-foreground group-hover:border-foreground/10">
                        <Pencil className="h-3 w-3" />
                      </div>
                      <span className="text-sm tracking-tight text-foreground">Rename</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    asChild
                    onClick={() => setIsArchiveOpen(true)}
                    className="rounded-lg cursor-pointer py-1 px-2 monochrome-hover transition-colors group"
                  >
                    <div className="flex items-center w-full">
                      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-transparent mr-1.5 text-foreground group-hover:border-foreground/10">
                        <Archive className="h-3 w-3" />
                      </div>
                      <span className="text-sm tracking-tight text-foreground">Archive</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    asChild
                    onClick={() => setIsDeleteOpen(true)}
                    className="rounded-lg cursor-pointer py-1 px-2 monochrome-hover transition-colors group"
                  >
                    <div className="flex items-center w-full">
                      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-transparent mr-1.5 text-foreground group-hover:border-foreground/10">
                        <Trash2 className="h-3 w-3" />
                      </div>
                      <span className="text-sm tracking-tight text-foreground">Delete</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {item.action && !chatId && (
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                {item.action}
              </span>
            )}
          </>
        )}
      </Link>

      {/* Rename Dialog */}
      <Dialog open={isRenameOpen} onOpenChange={setIsRenameOpen}>
        <DialogContent className="sm:max-w-md border-white/10 bg-background/80 backdrop-blur-2xl rounded-4xl">
          <DialogHeader>
            <DialogTitle>{tActions("rename")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" className="text-xs font-bold tracking-widest opacity-50">
                {t("dialog.label")}
              </Label>
              <Input
                id="name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="!bg-transparent border border-muted-foreground/30 dark:border-white/20 h-12 rounded-xl focus:border-black dark:focus:border-white focus:ring-0 focus-visible:ring-0"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setIsRenameOpen(false)}
              className="rounded-xl h-8 border-none bg-muted hover:bg-muted dark:hover:bg-muted text-foreground hover:text-foreground"
            >
              {tActions("cancel")}
            </Button>
            <Button
              onClick={handleRename}
              disabled={isProcessing || !newName.trim() || newName === item.title}
              className="rounded-xl h-8 text-white bg-black hover:bg-foreground/90 dark:disabled:bg-foreground/5 dark:hover:bg-foreground/5"
            >
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tActions("validate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation */}
      <AlertDialog open={isArchiveOpen} onOpenChange={setIsArchiveOpen}>
        <AlertDialogContent className="border-white/10 bg-background/80 backdrop-blur-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-black uppercase italic tracking-tighter text-xl">
              {tActions("archive")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-foreground/80">
              {t("dialog.conv_archive_prompt")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5 rounded-xl">
              {tActions("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleArchive()
              }}
              disabled={isProcessing}
              className="bg-foreground text-background hover:bg-foreground/90 rounded-xl font-bold"
            >
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tActions("validate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent className="border-white/10 bg-background/80 backdrop-blur-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-black uppercase italic tracking-tighter text-xl text-destructive">
              {tActions("deletion")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-foreground/80">
              {t("dialog.conv_delete_prompt")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5 rounded-xl">
              {tActions("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                handleDelete()
              }}
              disabled={isProcessing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl font-bold"
            >
              {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {tActions("validate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export function Sidebar({
  navigation,
  isCollapsed,
  onToggleCollapse,
  user,
  onSignOut,
  branding,
}: SidebarProps) {
  const pathname = usePathname()
  const t = useTranslations("appbar")

  const isRouteSelected = React.useCallback(
    (segment: string) => {
      if (!pathname || !segment) return false
      const cleanPathname = pathname.replace(/^\/+|\/+$/g, "")
      const cleanSegment = segment.replace(/^\/+|\/+$/g, "")
      return (
        cleanPathname === cleanSegment ||
        cleanPathname.startsWith(`${cleanSegment}/`)
      )
    },
    [pathname]
  )

  const userInitials = React.useMemo(() => {
    if (!user?.name) return "U"
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }, [user?.name])

  const renderNav = (isMobile = false) => (
    <nav className="flex flex-col gap-1">
      {navigation.filter(Boolean).map((item, index) => {
        if (isNavDivider(item)) {
          return <div key={`divider-${index}`} className="my-2 border-t border-muted-foreground/10 mx-2" />
        }

        if (isNavHeader(item)) {
          if (isCollapsed && !isMobile) return null
          return (
            <div
              key={`header-${index}`}
              className="px-2.5 pt-4 pb-1 text-[10px] font-bold text-foreground/50 uppercase tracking-[0.2em] mt-3 first:mt-0"
            >
              {item.title}
            </div>
          )
        }

        if (isNavItem(item)) {
          const isConversationItem = item.segment?.startsWith("account/chat/")

          // Hide discussions when sidebar is collapsed
          if (isCollapsed && !isMobile && isConversationItem) return null

          // "New Chat" (account/chat without ID) should never be selected
          const isNewChatItem = item.segment === "account/chat"
          const shouldBeSelected = isNewChatItem ? false : isRouteSelected(item.segment)

          const content = (
            <div key={item.segment} className="relative">
              <NavItemComponent
                item={item}
                isCollapsed={isCollapsed && !isMobile}
                isSelected={shouldBeSelected}
              />
            </div>
          )

          if (isCollapsed && !isMobile) {
            return (
              <Tooltip key={item.segment}>
                <TooltipTrigger asChild>
                  {content}
                </TooltipTrigger>
                <TooltipContent side="right">
                  {item.title}
                </TooltipContent>
              </Tooltip>
            )
          }

          return content
        }

        return null
      })}
    </nav>
  )

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          "h-full border-r bg-card/70 backdrop-blur-xl transition-all duration-300 ease-in-out hidden md:flex flex-col shrink-0",
          isCollapsed ? "w-14" : "w-56"
        )}
      >
        {/* Header */}
        <div className={cn(
          "flex h-14 items-center px-3 shrink-0",
          isCollapsed ? "justify-center" : "justify-between"
        )}>
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggleCollapse}
                  className="h-10 w-10 rounded-xl hover:bg-muted"
                >
                  <img src="/djotta-logo-rounded.webp" alt="Djotta" className="h-7 w-7 rounded-lg" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Ouvrir la barre latérale</TooltipContent>
            </Tooltip>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl">
                  <img src="/djotta-logo-rounded.webp" alt="Djotta" className="h-7 w-7 rounded-lg" />
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onToggleCollapse}
                    className="h-9 w-9 text-foreground hover:text-foreground hover:bg-muted rounded-xl"
                  >
                    <PanelLeft className="h-5 w-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Fermer la barre latérale</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 min-h-0 overflow-y-auto py-2 no-scrollbar">
          <div className="px-2">
            {renderNav()}
          </div>
        </div>

        {/* User Account / Footer */}
        {user && (
          <div className={isCollapsed ? "" : "p-3 bg-background/10 backdrop-blur"}>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "w-full flex items-center justify-between gap-3 rounded-xl px-2 py-2 monochrome-hover transition-all duration-300 active:scale-[0.98] group",
                    isCollapsed && "justify-center rounded-full p-1 h-12 w-12 mx-auto"
                  )}
                >
                  <div className={cn("flex items-center gap-2", isCollapsed && "justify-center gap-0")}>
                    <Avatar className={cn(
                      "h-8 w-8 ring-1 ring-foreground/5 transition-all duration-300 group-hover:ring-foreground/10 shrink-0",
                      isCollapsed && "h-8 w-8 ring-0 shadow-sm"
                    )}>
                      <AvatarImage src={user.image || undefined} alt={user.name || "User"} />
                      <AvatarFallback className="bg-muted text-foreground text-[min(10px,2.5vw)] font-black uppercase flex items-center justify-center overflow-hidden">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    {!isCollapsed && (
                      <div className="flex flex-1 flex-col items-start text-left overflow-hidden">
                        <span className="text-xs font-bold tracking-tight text-foreground leading-tight truncate w-full">
                          {user.name}
                        </span>
                        <span className="text-[10px] font-medium text-foreground opacity-40 lowercase tracking-tight truncate w-full">
                          {user.email}
                        </span>
                      </div>
                    )}
                  </div>
                  {!isCollapsed && (
                    <SettingsSVG className="h-6 w-6 text-foreground opacity-20 group-hover:opacity-60 transition-all duration-300 group-hover:rotate-45" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align={isCollapsed ? "start" : "center"}
                side="top"
                sideOffset={8}
                className={cn(
                  "p-1.5 shadow-2xl border border-muted-foreground/20 dark:border-border/50 backdrop-blur-3xl bg-card/90 dark:bg-zinc-900/90 rounded-2xl",
                  isCollapsed ? "w-48" : "w-[var(--radix-dropdown-menu-trigger-width)]"
                )}
              >
                <DropdownMenuLabel className="px-2 py-1.5">
                  <div className="flex flex-col space-y-0.5">
                    <p className="text-xs text-foreground tracking-[0.2em] truncate">{user.name}</p>
                    <p className="text-[9px] font-medium text-foreground lowercase truncate">
                      {user.email}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="mx-1 my-1 opacity-50" />
                <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-1 px-2 monochrome-hover transition-colors group">
                  <Link href="/account/profile" className="flex items-center w-full">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-transparent mr-2.5 text-foreground group-hover:border-foreground/10">
                      <User className="h-3 w-3" />
                    </div>
                    <span className="text-sm tracking-tight text-foreground">Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-1 px-2 monochrome-hover transition-colors group">
                  <Link href="/account/settings" className="flex items-center w-full">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-transparent mr-2.5 text-foreground group-hover:border-foreground/10">
                      <SettingsSVG className="h-3 w-3" />
                    </div>
                    <span className="text-sm tracking-tight text-foreground">Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="mx-1 my-1 opacity-50" />
                <DropdownMenuItem
                  className="rounded-lg cursor-pointer py-1 px-2 monochrome-hover transition-colors group"
                  onClick={onSignOut}
                >
                  <div className="flex h-6 w-6 items-center justify-center rounded-md bg-transparent mr-2.5 text-foreground group-hover:border-foreground/10">
                    <LogOut className="h-3 w-3" />
                  </div>
                  <span className="text-sm tracking-tight text-foreground">Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </aside>
    </TooltipProvider>
  )
}

interface MobileSidebarProps extends Omit<SidebarProps, "isCollapsed" | "onToggleCollapse"> {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MobileSidebar({
  navigation,
  branding,
  user,
  onSignOut,
  open,
  onOpenChange,
}: MobileSidebarProps) {
  const pathname = usePathname()
  const userInitials = React.useMemo(() => {
    if (!user?.name) return "U"
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }, [user?.name])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-52 p-0 flex flex-col border-r-0 bg-card/70 backdrop-blur-xl overflow-visible">
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

        {/* Header */}
        <div className="flex h-12 items-center border-b border-foreground/5 px-4 shrink-0 bg-background/20 backdrop-blur-md">
          {branding?.title && (
            <Link
              href={branding.homeUrl || "/"}
              className="flex items-center gap-2 font-semibold"
              onClick={() => onOpenChange(false)}
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-foreground text-background">
                {branding.logo}
              </div>
              <span className="text-sm font-bold tracking-tight">{branding.title}</span>
            </Link>
          )}
        </div>

        {/* Navigation */}
        <div className="flex-1 min-h-0 overflow-y-auto py-4 no-scrollbar">
          <div className="px-3">
            <nav className="flex flex-col gap-0.5">
              {navigation.filter(Boolean).map((item, index) => {
                if (isNavDivider(item)) {
                  return <div key={`divider-${index}`} className="my-1 border-t border-foreground/5 mx-2" />
                }

                if (isNavHeader(item)) {
                  return (
                    <div
                      key={`header-${index}`}
                      className="px-3 pt-4 pb-1 text-[10px] font-bold text-foreground/50 uppercase tracking-[0.2em] mt-2 first:mt-0"
                    >
                      {item.title}
                    </div>
                  )
                }

                if (isNavItem(item)) {
                  const Icon = item.icon
                  const isNewChatItem = item.segment === "account/chat"
                  const isConversationItem = item.segment?.startsWith("account/chat/")
                  const isSelected = isNewChatItem
                    ? false
                    : (item.segment && pathname.includes(item.segment))

                  return (
                    <Link
                      key={item.segment}
                      href={`/${item.segment}`}
                      onClick={() => onOpenChange(false)}
                      className={cn(
                        "flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-xs transition-all duration-200",
                        // New Chat item: normal style, never selected
                        isNewChatItem
                          ? "text-foreground monochrome-hover"
                          : isConversationItem
                            ? cn(
                              "monochrome-hover",
                              isSelected ? "bg-muted text-foreground font-bold" : "text-foreground/60"
                            )
                            : cn(
                              "monochrome-hover",
                              isSelected ? "bg-foreground text-background shadow-md font-black" : "text-foreground/80 active:bg-muted"
                            )
                      )}
                    >
                      {/* Only show icon for non-conversation items */}
                      {Icon && !isConversationItem && (
                        <span className={cn(
                          "flex h-4 w-4 shrink-0 items-center justify-center",
                          isNewChatItem ? "text-foreground" : (isSelected ? "text-background" : "text-foreground/60")
                        )}>
                          {React.isValidElement(Icon) ? Icon : (typeof Icon === 'function' ? <Icon className="h-4 w-4" /> : null)}
                        </span>
                      )}
                      <span className="flex-1 truncate">{item.title}</span>
                    </Link>
                  )
                }

                return null
              })}
            </nav>
          </div>
        </div>

        {/* User Account / Footer */}
        {user && (
          <div className="shrink-0 p-2 border-t border-foreground/5 bg-card/80 backdrop-blur-xl">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 monochrome-hover transition-all duration-300 active:scale-[0.98] group"
                >
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7 ring-1 ring-foreground/5 transition-all duration-300 group-hover:ring-foreground/10 shrink-0">
                      <AvatarImage src={user.image || undefined} alt={user.name || "User"} />
                      <AvatarFallback className="bg-muted text-foreground text-[8px] font-black uppercase flex items-center justify-center overflow-hidden">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-1 flex-col items-start text-left overflow-hidden">
                      <span className="text-[10px] font-bold tracking-tight text-foreground leading-tight truncate w-full">
                        {user.name}
                      </span>
                    </div>
                  </div>
                  <SettingsSVG className="h-2.5 w-2.5 text-foreground opacity-20 group-hover:opacity-60 transition-all duration-300 group-hover:rotate-45" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="right"
                sideOffset={8}
                className="w-48 p-1 shadow-2xl border-border/50 backdrop-blur-3xl bg-card/90 dark:bg-zinc-900/90 rounded-xl z-[60]"
              >
                <DropdownMenuLabel className="px-2 py-1.5 text-[10px] font-bold text-muted-foreground/60 uppercase tracking-[0.2em]">
                  {user.name}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="mx-1 my-1 opacity-50" />
                <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-1.5 px-2 monochrome-hover transition-colors group">
                  <Link href="/account/profile" className="flex items-center w-full" onClick={() => onOpenChange(false)}>
                    <div className="flex h-5 w-5 items-center justify-center rounded-md border border-foreground/5 bg-transparent mr-2 text-foreground group-hover:border-foreground/10">
                      <User className="h-3 w-3" />
                    </div>
                    <span className="font-bold text-xs tracking-tight text-foreground">Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg cursor-pointer py-1.5 px-2 monochrome-hover transition-colors group">
                  <Link href="/account/settings" className="flex items-center w-full" onClick={() => onOpenChange(false)}>
                    <div className="flex h-5 w-5 items-center justify-center rounded-md border border-foreground/5 bg-transparent mr-2 text-foreground group-hover:border-foreground/10">
                      <SettingsSVG className="h-3 w-3" />
                    </div>
                    <span className="font-bold text-xs tracking-tight text-foreground">Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="mx-1 my-1 opacity-50" />
                <DropdownMenuItem
                  className="rounded-lg cursor-pointer py-1.5 px-2 monochrome-hover transition-colors group"
                  onClick={onSignOut}
                >
                  <div className="flex h-5 w-5 items-center justify-center rounded-md border border-foreground/5 bg-transparent mr-2 text-foreground group-hover:border-foreground/10">
                    <LogOut className="h-3 w-3" />
                  </div>
                  <span className="font-bold text-xs tracking-tight text-foreground">Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

export { Menu as MenuIcon }
